'use client';

import React, { useEffect, useRef, useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { getTranslations } from '@/lib/i18n';

export default function BookSection() {
  const { language } = useLanguage();
  const t = getTranslations(language)?.book;

  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setVisible(true); },
      { threshold: 0.2 }
    );
    if (ref?.current) obs?.observe(ref?.current);
    return () => obs?.disconnect();
  }, []);

  return (
    <section id="book" ref={ref} className="section-glow py-32 px-6">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">

          {/* Left: copy + chips */}
          <div className={`flex flex-col gap-8 ${visible ? 'reveal-up is-visible' : 'reveal-up'}`}>
            <div>
              <span className="chip mb-4 inline-flex">{t?.chip}</span>
              <h2 className="font-manrope font-extrabold text-dispatch-text leading-tight tracking-tight mt-4"
                style={{ fontSize: 'clamp(2.2rem, 5vw, 3.5rem)' }}>
                {t?.headline1}<br />
                <span className="text-dispatch-faint line-through decoration-dispatch-cyan">{t?.headline2}</span>
                <span className="text-dispatch-cyan"> {t?.headline3}</span>
              </h2>
              <p className="text-dispatch-muted text-lg font-dm-sans leading-relaxed mt-4 max-w-md">
                {t?.subtitle}
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              {t?.chips?.map((c) => (
                <span key={c} className="chip">{c}</span>
              ))}
            </div>

            <a href="#download" className="self-start flex items-center gap-3 px-6 py-3.5 bg-dispatch-cyan text-dispatch-bg rounded-xl font-bold font-mono text-sm tracking-wide hover:bg-white transition-all duration-300 shadow-cyan-sm">
              {t?.cta}
            </a>
          </div>

          {/* Right: service grid mockup */}
          <div className={`${visible ? 'reveal-blur is-visible' : 'reveal-blur'}`}>
            <div className="glass-panel rounded-2xl overflow-hidden border border-dispatch-cyan/20 shadow-card">
              <div className="px-5 py-4 border-b border-dispatch-surface flex items-center justify-between">
                <span className="font-mono text-dispatch-cyan text-sm font-semibold">{t?.appHeader}</span>
                <span className="chip">Chicago, IL</span>
              </div>

              <div className="px-5 py-3 border-b border-dispatch-surface">
                <div className="flex items-center gap-3 bg-dispatch-bg/60 rounded-xl px-4 py-3 border border-dispatch-surface">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-dispatch-faint">
                    <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
                  </svg>
                  <span className="font-mono text-dispatch-faint text-sm">{t?.searchPlaceholder}</span>
                  <span className="cursor-blink text-dispatch-cyan font-mono text-sm ml-auto">&nbsp;</span>
                </div>
              </div>

              <div className={`p-5 grid grid-cols-3 gap-3 stagger-grid ${visible ? 'is-visible' : ''}`}>
                {t?.services?.map((s) => (
                  <div
                    key={s?.name}
                    className="shine-card flex flex-col items-center gap-2 p-4 rounded-xl bg-dispatch-bg/50 border border-dispatch-surface hover:border-dispatch-cyan/40 transition-all duration-300 cursor-pointer group"
                  >
                    <span className="text-2xl">{s?.icon}</span>
                    <span className="font-manrope font-semibold text-dispatch-text text-xs text-center">{s?.name}</span>
                    <span className="font-mono text-dispatch-faint text-[9px]">{s?.count}</span>
                  </div>
                ))}
              </div>

              <div className="px-5 pb-5">
                <div className="bg-dispatch-cyan/10 border border-dispatch-cyan/30 rounded-xl px-4 py-3 flex items-center justify-between">
                  <div>
                    <p className="text-dispatch-cyan font-mono font-semibold text-sm">{t?.available}</p>
                    <p className="text-dispatch-muted font-mono text-xs">{t?.earliest}</p>
                  </div>
                  <button className="bg-dispatch-cyan text-dispatch-bg px-4 py-2 rounded-lg font-bold font-mono text-xs hover:bg-white transition-colors">
                    {t?.bookBtn}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}